import { computed }                       from 'mobx';
import { makeAutoObservable, observable } from 'mobx';
import { PagedCollection }                from 'collections/PagedCollection';
import { ApiCollection }                  from 'collections/ApiCollection';
import LayoutTypeModel                    from 'models/LayoutTypeModel';
import StandLayoutModel                   from 'models/StandLayoutModel';
import ShowModel                          from 'models/ShowModel';
import StandModel                         from 'models/StandModel';
import currentFilters                     from 'utils/currentFilters';
import currentSession                     from 'utils/currentSession';

const ITEMS_PER_PAGE = 50;

export default class StandListStore {
  @observable
  public rawFilters: Record<string, any> = {};

  @observable
  public sorts: Record<string, any> = {};

  @observable public layoutTypeCollection = new ApiCollection(LayoutTypeModel);
  @observable public show = new ShowModel();
  @observable public standCollection = new PagedCollection(StandModel);
  @observable public standCollectionFull = new ApiCollection(StandModel);
  @observable public standCollectionWithSecurityOption = new ApiCollection(StandModel);
  @observable public standLayoutCollection = new ApiCollection(StandLayoutModel);

  constructor() {
    makeAutoObservable(this);
  }

  public clear = () => {
    this.show.clear();
    this.standCollection.clear();
    this.standCollectionFull.clear();
    this.standCollectionWithSecurityOption.clear();
  };

  public fetchStandsData = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: StandModel['filters'],
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    this.standCollection.itemsPerPage = pagination.pageSize || ITEMS_PER_PAGE;
    this.standCollection.setPage(pagination.current || 1);

    // remove all filters
    this.standCollection.filters = {};

    // set filters
    Object.keys(apiFilters).forEach(filterName => {
      const key = filterName as keyof StandModel['filters'];
      this.standCollection.setFilter(key, apiFilters[key]);
    });

    this.standCollection.setFilter('show', showId);

    // remove all sorts
    this.standCollection.sorts = {};

    // set sorts
    Object.keys(sorter).forEach(sortName => {
      this.standCollection.setSort(sortName, sorter[sortName] === 'asc');
    });

    this.standCollectionFull.setFilters({
      properties: [
        'aisle',
        'hall',
        'id',
        'nbClosedReports',
        'nbInProgressReports',
        'number',
        'standSecurityInstruction',
        'standType',
        'status',
        'unSeen',
        'treated',
        'documentsTreated',
      ],
      show: showId,
    });

    await this.standCollection.list();

    await Promise.all([
      this.standCollectionFull
        .list(),

      this.standCollectionWithSecurityOption
        .setFilters({
          show: showId,
          'standSecurityInstruction.builtByOne': false,
        })
        .list(),

      this.standLayoutCollection
        .setFilter('stand', this.standCollection.getUniques('id'))
        .list(),
    ]);
  };

  public fetchTypesAndStatuses = async () => {
    await Promise.all([
      this.layoutTypeCollection
        .list(),
    ]);
  };

  public initFromLocalStorage = async () => {
    if (!currentFilters.isLoaded) {
      await currentFilters.load(currentSession.user.user_id);
    }
    this.rawFilters = currentFilters.getFiltersForPath(window.location.pathname);
    this.sorts = currentFilters.getSortsForPath(window.location.pathname);
  }

  public load = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: StandModel['filters'],
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    await Promise.all([
      this.fetchStandsData(showId, pagination, apiFilters, sorter),

      this.show
        .set('id', showId)
        .fetch(),

      this.fetchTypesAndStatuses(),
    ]);
  };

  public setAndSaveRawFilters = async (filters: Record<string, any>, sorts?: any) => {
    this.rawFilters = filters;
    if (sorts) this.sorts = sorts;

    await currentFilters.saveFiltersAndSorts(
      window.location.pathname,
      this.rawFilters,
      this.sorts
    );
  }

  @computed
  public get apiFilters() {
    return this._processFilters(this.rawFilters);
  }

  private _processFilters = (filters: Record<string, any>): StandModel['filters'] => {
    const processed = { ...filters };

    if (processed.surface) {
      const surfaceValues = Array.isArray(processed.surface) ? processed.surface : [processed.surface];
      let min: number | undefined;
      let max: number | undefined;

      surfaceValues.forEach((value: string) => {
        if (typeof value === 'string' && value.includes('_to_')) {
          const [vMin, vMax] = value.split('_to_').map(v => v ? parseInt(v, 10) : undefined);

          if (vMin !== undefined && (min === undefined || vMin < min)) {
            min = vMin;
          }
          if (vMax !== undefined && (max === undefined || vMax > max)) {
            max = vMax;
          } else if (value.endsWith('_to_')) {
            // Cas particulier pour "+50" (51_to_) : on ne veut plus de borne max
            max = undefined;
          }
        }
      });

      processed['surface[gte]'] = min;
      processed['surface[lte]'] = max;

      delete processed.surface;
    }

    return processed;
  };
}
